import api from '../../../api';

/**
 * Convert period to year for sales target lookup
 * @param {string} period - Period string (this_year, last_year, etc.)
 * @returns {number|null} Year number or null if period doesn't map to a year
 */
const convertPeriodToYear = (period) => {
    if (!period || period === 'all') {
        return new Date().getFullYear(); // Default to current year
    }
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11
    
    switch (period) {
        case 'this_year':
            return currentYear;
        case 'last_year':
            return currentYear - 1;
        case 'this_month':
        case 'last_month':
        case 'this_quarter':
        case 'last_quarter':
            // For month/quarter periods, use current year
            return currentYear;
        case 'custom':
            // For custom periods, we'd need to extract year from date range
            // For now, default to current year
            return currentYear;
        default:
            return currentYear;
    }
};

/**
 * Fetch sales achievement data from invoice data
 * This now uses a dedicated backend endpoint that performs all aggregation in the database
 * @param {Object} filters - Filter parameters (period, creator, service, productCategory, standard)
 * @returns {Promise<Object>} Sales achievement data with metrics
 */
export const fetchSalesAchievementData = async (filters = {}) => {
    try {
        // Convert period to year for sales target lookup (still needed for target calculation)
        const year = convertPeriodToYear(filters.period);
        
        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('period', filters.period || 'this_year'); // Send period for date range filtering

        // Handle multi-select filters (arrays)
        const creatorValue = Array.isArray(filters.creator) && filters.creator.length > 0
            ? filters.creator.join(',')
            : 'all';
        const serviceValue = Array.isArray(filters.service) && filters.service.length > 0
            ? filters.service.join(',')
            : 'all';
        const productCategoryValue = Array.isArray(filters.productCategory) && filters.productCategory.length > 0
            ? filters.productCategory.join(',')
            : 'all';
        const standardValue = Array.isArray(filters.standard) && filters.standard.length > 0
            ? filters.standard.join(',')
            : 'all';
        const departmentIdValue = Array.isArray(filters.departmentId) && filters.departmentId.length > 0
            ? filters.departmentId.join(',')
            : 'all';
        const branchIdValue = filters.branchId ? String(filters.branchId) : 'all';
        const taggingIdsValue = Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0
            ? filters.taggingIds.join(',')
            : 'all';

        queryParams.append('creator', creatorValue);
        queryParams.append('service', serviceValue);
        queryParams.append('productCategory', productCategoryValue);
        queryParams.append('standard', standardValue);
        queryParams.append('departmentId', departmentIdValue);
        queryParams.append('branchId', branchIdValue);
        queryParams.append('taggingIds', taggingIdsValue);
        if (year) {
            queryParams.append('year', year.toString()); // Still send year for sales target lookup
        }
        
        // Add custom date range if period is custom
        if (filters.period === 'custom') {
            if (filters.startDate) {
                queryParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                queryParams.append('endDate', filters.endDate);
            }
        }
        
        // ✅ Add permission-based filtering parameters
        if (filters.viewDepartmentId) {
            queryParams.append('viewDepartmentId', filters.viewDepartmentId);
        }
        if (filters.viewOwnUserId) {
            queryParams.append('viewOwnUserId', filters.viewOwnUserId);
        }
        
        // Single optimized API call - all aggregation is done in the database
        const response = await api.get(`/sales-report/sales-achievement?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch sales by service item data from proposals with status "won" (proposalStageId = 5)
 * This now uses a dedicated backend endpoint that performs all aggregation in the database
 * @param {Object} filters - Filter parameters (period, salesCreator, serviceCategory, standard, serviceType)
 * @returns {Promise<Object>} Sales by service item data with metrics
 */
export const fetchSalesByServiceItemData = async (filters = {}) => {
    try {
        // Determine year for service item filtering
        let year = null;
        if (filters.period === 'custom' && filters.startDate) {
            // For custom periods, extract year from start date
            const startDate = new Date(filters.startDate);
            year = startDate.getFullYear();
        } else {
            // For other periods, use the period-based year
            year = convertPeriodToYear(filters.period);
        }

        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('period', filters.period || 'this_year');

        // Handle multi-select filters (arrays)
        const salesCreatorValue = Array.isArray(filters.salesCreator) && filters.salesCreator.length > 0
            ? filters.salesCreator.join(',')
            : 'all';
        const serviceCategoryValue = Array.isArray(filters.serviceCategory) && filters.serviceCategory.length > 0
            ? filters.serviceCategory.join(',')
            : 'all';
        const productCategoryValue = Array.isArray(filters.productCategory) && filters.productCategory.length > 0
            ? filters.productCategory.join(',')
            : 'all';
        const standardValue = Array.isArray(filters.standard) && filters.standard.length > 0
            ? filters.standard.join(',')
            : 'all';
        const departmentIdValue = Array.isArray(filters.departmentId) && filters.departmentId.length > 0
            ? filters.departmentId.join(',')
            : 'all';
        const branchIdValue = filters.branchId ? String(filters.branchId) : 'all';
        const taggingIdsValue = Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0
            ? filters.taggingIds.join(',')
            : 'all';

        queryParams.append('salesCreator', salesCreatorValue);
        queryParams.append('serviceCategory', serviceCategoryValue);
        queryParams.append('productCategory', productCategoryValue);
        queryParams.append('standard', standardValue);
        queryParams.append('departmentId', departmentIdValue);
        queryParams.append('branchId', branchIdValue);
        queryParams.append('taggingIds', taggingIdsValue);
        queryParams.append('serviceType', filters.serviceType || 'all');

        // Send year for service item filtering (only service items for the specified year)
        if (year) {
            queryParams.append('year', year.toString());
        }

        // Add custom date range if period is custom
        if (filters.period === 'custom') {
            if (filters.startDate) {
                queryParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                queryParams.append('endDate', filters.endDate);
            }
        }
        
        // ✅ Add permission-based filtering parameters
        if (filters.viewDepartmentId) {
            queryParams.append('viewDepartmentId', filters.viewDepartmentId);
        }
        if (filters.viewOwnUserId) {
            queryParams.append('viewOwnUserId', filters.viewOwnUserId);
        }
        
        // Single optimized API call - all aggregation is done in the database
        const response = await api.get(`/sales-report/service-items?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Total Revenue tab — single backend contract (won-proposal revenue + list + summary cards).
 * @param {Object} achievementFilters - Sales achievement-style filters: period, creator, service, productCategory, standard, departmentId, startDate, endDate, viewDepartmentId, viewOwnUserId
 * @param {Object} serviceFilters - Service-item-style filters: salesCreator, serviceCategory, standard, departmentId, serviceType (period/start/end should match achievementFilters when applied from UI)
 * @returns {Promise<Object>} summary, achievementMetrics, metrics, salesData, reportingCurrency, exchangeRateBasis
 */
export const fetchTotalRevenueData = async (achievementFilters = {}, serviceFilters = {}) => {
    const period = achievementFilters.period || serviceFilters.period || 'this_year';

    let year = null;
    if (period === 'custom' && (achievementFilters.startDate || serviceFilters.startDate)) {
        const start = achievementFilters.startDate || serviceFilters.startDate;
        year = new Date(start).getFullYear();
    } else {
        year = convertPeriodToYear(period);
    }

    const queryParams = new URLSearchParams();
    queryParams.append('period', period);

    const creatorValue = Array.isArray(achievementFilters.creator) && achievementFilters.creator.length > 0
        ? achievementFilters.creator.join(',')
        : 'all';
    const serviceValue = Array.isArray(achievementFilters.service) && achievementFilters.service.length > 0
        ? achievementFilters.service.join(',')
        : 'all';
    const productCategoryValue = Array.isArray(achievementFilters.productCategory) && achievementFilters.productCategory.length > 0
        ? achievementFilters.productCategory.join(',')
        : 'all';
    const achievementStandardValue = Array.isArray(achievementFilters.standard) && achievementFilters.standard.length > 0
        ? achievementFilters.standard.join(',')
        : 'all';
    const achievementDepartmentValue = Array.isArray(achievementFilters.departmentId) && achievementFilters.departmentId.length > 0
        ? achievementFilters.departmentId.join(',')
        : 'all';
    const achievementBranchValue = achievementFilters.branchId ? String(achievementFilters.branchId) : 'all';
    const achievementTaggingValue = Array.isArray(achievementFilters.taggingIds) && achievementFilters.taggingIds.length > 0
        ? achievementFilters.taggingIds.join(',')
        : 'all';

    const salesCreatorValue = Array.isArray(serviceFilters.salesCreator) && serviceFilters.salesCreator.length > 0
        ? serviceFilters.salesCreator.join(',')
        : 'all';
    const serviceCategoryValue = Array.isArray(serviceFilters.serviceCategory) && serviceFilters.serviceCategory.length > 0
        ? serviceFilters.serviceCategory.join(',')
        : 'all';
    const standardValue = Array.isArray(serviceFilters.standard) && serviceFilters.standard.length > 0
        ? serviceFilters.standard.join(',')
        : 'all';
    const serviceDepartmentValue = Array.isArray(serviceFilters.departmentId) && serviceFilters.departmentId.length > 0
        ? serviceFilters.departmentId.join(',')
        : 'all';

    queryParams.append('creator', creatorValue);
    queryParams.append('service', serviceValue);
    queryParams.append('productCategory', productCategoryValue);
    queryParams.append('achievementStandard', achievementStandardValue);
    queryParams.append('achievementDepartmentId', achievementDepartmentValue);
    queryParams.append('branchId', achievementBranchValue);
    queryParams.append('taggingIds', achievementTaggingValue);

    queryParams.append('salesCreator', salesCreatorValue);
    queryParams.append('serviceCategory', serviceCategoryValue);
    queryParams.append('standard', standardValue);
    queryParams.append('departmentId', serviceDepartmentValue);
    queryParams.append('serviceType', serviceFilters.serviceType || 'all');

    if (year != null) {
        queryParams.append('year', String(year));
    }

    if (period === 'custom') {
        const startDate = achievementFilters.startDate || serviceFilters.startDate;
        const endDate = achievementFilters.endDate || serviceFilters.endDate;
        if (startDate) queryParams.append('startDate', startDate);
        if (endDate) queryParams.append('endDate', endDate);
    }

    const viewDept = achievementFilters.viewDepartmentId ?? serviceFilters.viewDepartmentId;
    const viewOwn = achievementFilters.viewOwnUserId ?? serviceFilters.viewOwnUserId;
    if (viewDept) queryParams.append('viewDepartmentId', viewDept);
    if (viewOwn) queryParams.append('viewOwnUserId', viewOwn);

    const response = await api.get(`/sales-report/total-revenue?${queryParams.toString()}`);
    return response.data;
};

export const fetchContractWonData = async (filters = {}) => {
    const period = filters.period || 'this_year';
    const queryParams = new URLSearchParams();
    queryParams.append('period', period);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);

    const creator = Array.isArray(filters.creator) && filters.creator.length > 0
        ? filters.creator.join(',') : (filters.salesCreator || 'all');
    queryParams.append('creator', creator);

    const dept = Array.isArray(filters.departmentId) && filters.departmentId.length > 0
        ? filters.departmentId.join(',') : 'all';
    queryParams.append('departmentId', dept);

    const service = Array.isArray(filters.service) && filters.service.length > 0
        ? filters.service.join(',') : 'all';
    queryParams.append('service', service);

    const productCategory = Array.isArray(filters.productCategory) && filters.productCategory.length > 0
        ? filters.productCategory.join(',') : 'all';
    queryParams.append('productCategory', productCategory);

    const standard = Array.isArray(filters.standard) && filters.standard.length > 0
        ? filters.standard.join(',') : 'all';
    queryParams.append('standard', standard);

    if (filters.branchId) queryParams.append('branchId', String(filters.branchId));
    if (filters.taggingIds && Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0)
        queryParams.append('taggingIds', filters.taggingIds.join(','));
    if (filters.viewDepartmentId) queryParams.append('viewDepartmentId', filters.viewDepartmentId);
    if (filters.viewOwnUserId) queryParams.append('viewOwnUserId', filters.viewOwnUserId);

    const response = await api.get(`/sales-report/contract-won?${queryParams.toString()}`);
    return response.data;
};

/**
 * Fetch customer balance data from backend
 * This now uses a dedicated backend endpoint that performs all aggregation in the database
 * @param {Object} filters - Filter parameters (period, salesCreator, payment, status, startDate, endDate)
 * @returns {Promise<Array>} Customer balance data
 */
export const fetchCustomerBalanceData = async (filters = {}) => {
    try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('period', filters.period || 'this_year');

        // Handle multi-select filters (arrays)
        const salesCreatorValue = Array.isArray(filters.salesCreator) && filters.salesCreator.length > 0
            ? filters.salesCreator.join(',')
            : 'all';
        const paymentValue = Array.isArray(filters.payment) && filters.payment.length > 0
            ? filters.payment.join(',')
            : 'all';
        const statusValue = Array.isArray(filters.status) && filters.status.length > 0
            ? filters.status.join(',')
            : 'all';
        const serviceCategoryValue = Array.isArray(filters.serviceCategory) && filters.serviceCategory.length > 0
            ? filters.serviceCategory.join(',')
            : 'all';
        const productCategoryValue = Array.isArray(filters.productCategory) && filters.productCategory.length > 0
            ? filters.productCategory.join(',')
            : 'all';
        const standardValue = Array.isArray(filters.standard) && filters.standard.length > 0
            ? filters.standard.join(',')
            : 'all';
        const departmentIdValue = Array.isArray(filters.departmentId) && filters.departmentId.length > 0
            ? filters.departmentId.join(',')
            : 'all';
        const branchIdValue = filters.branchId ? String(filters.branchId) : 'all';
        const taggingIdsValue = Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0
            ? filters.taggingIds.join(',')
            : 'all';

        queryParams.append('salesCreator', salesCreatorValue);
        queryParams.append('payment', paymentValue);
        queryParams.append('status', statusValue);
        queryParams.append('serviceCategory', serviceCategoryValue);
        queryParams.append('productCategory', productCategoryValue);
        queryParams.append('standard', standardValue);
        queryParams.append('departmentId', departmentIdValue);
        queryParams.append('branchId', branchIdValue);
        queryParams.append('taggingIds', taggingIdsValue);
        if (filters.startDate) {
            queryParams.append('startDate', filters.startDate);
        }
        if (filters.endDate) {
            queryParams.append('endDate', filters.endDate);
        }

        // ✅ Add permission-based filtering parameters
        if (filters.viewDepartmentId) {
            queryParams.append('viewDepartmentId', filters.viewDepartmentId);
        }
        if (filters.viewOwnUserId) {
            queryParams.append('viewOwnUserId', filters.viewOwnUserId);
        }

        // Single optimized API call - all aggregation is done in the database
        const response = await api.get(`/sales-report/customer-balance?${queryParams.toString()}`);
        
        // Return the data array from the response
        return response.data?.data || [];
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch sales list detail data from backend
 * This now uses a dedicated backend endpoint that performs all aggregation in the database
 * @param {Object} filters - Filter parameters (period, salesCreator, serviceCategory, payment)
 * @returns {Promise<Array>} Sales list detail data
 */
export const fetchSalesListDetailData = async (filters = {}) => {
    try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('period', filters.period || 'this_year');

        // Handle multi-select filters (arrays)
        const salesCreatorValue = Array.isArray(filters.salesCreator) && filters.salesCreator.length > 0
            ? filters.salesCreator.join(',')
            : 'all';
        const serviceCategoryValue = Array.isArray(filters.serviceCategory) && filters.serviceCategory.length > 0
            ? filters.serviceCategory.join(',')
            : 'all';
        const productCategoryValue = Array.isArray(filters.productCategory) && filters.productCategory.length > 0
            ? filters.productCategory.join(',')
            : 'all';
        const standardValue = Array.isArray(filters.standard) && filters.standard.length > 0
            ? filters.standard.join(',')
            : 'all';
        const paymentValue = Array.isArray(filters.payment) && filters.payment.length > 0
            ? filters.payment.join(',')
            : 'all';
        const departmentIdValue = Array.isArray(filters.departmentId) && filters.departmentId.length > 0
            ? filters.departmentId.join(',')
            : 'all';
        const branchIdValue = filters.branchId ? String(filters.branchId) : 'all';
        const taggingIdsValue = Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0
            ? filters.taggingIds.join(',')
            : 'all';

        queryParams.append('salesCreator', salesCreatorValue);
        queryParams.append('serviceCategory', serviceCategoryValue);
        queryParams.append('productCategory', productCategoryValue);
        queryParams.append('standard', standardValue);
        queryParams.append('payment', paymentValue);
        queryParams.append('departmentId', departmentIdValue);
        queryParams.append('branchId', branchIdValue);
        queryParams.append('taggingIds', taggingIdsValue);

        // Add custom date range if period is custom
        if (filters.period === 'custom') {
            if (filters.startDate) {
                queryParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                queryParams.append('endDate', filters.endDate);
            }
        }

        // ✅ Add permission-based filtering parameters
        if (filters.viewDepartmentId) {
            queryParams.append('viewDepartmentId', filters.viewDepartmentId);
        }
        if (filters.viewOwnUserId) {
            queryParams.append('viewOwnUserId', filters.viewOwnUserId);
        }

        // Single optimized API call - all aggregation is done in the database
        const response = await api.get(`/sales-report/sales-list-detail?${queryParams.toString()}`);
        
        // Return the data array from the response
        return response.data?.data || [];
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch incentive claim calculation from backend.
 * Returns salesName, period, items (with productName, productTagging, calculated incentives), and totals.
 * @param {Object} filters - period, startDate, endDate, creatorIds (array of user IDs)
 * @returns {Promise<Object>} IncentiveClaimCalculationResponse
 */
export const fetchIncentiveClaimData = async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.period) queryParams.append('period', filters.period);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);

    if (filters.branchId) queryParams.append('branchId', String(filters.branchId));
    const taggingIds = Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0
        ? filters.taggingIds.join(',')
        : null;
    if (taggingIds) queryParams.append('taggingIds', taggingIds);

    const creatorIds = Array.isArray(filters.creatorIds) && filters.creatorIds.length > 0
        ? filters.creatorIds.join(',')
        : null;
    if (creatorIds) queryParams.append('creatorIds', creatorIds);

    const response = await api.get(`/crm/incentive-claims/calculate?${queryParams.toString()}`);
    return response.data;
};

/**
 * Fetch filter options (all users, all services, all product categories, all standards)
 * @param {Object} permissions - Permission parameters (canViewGlobal, canViewDepartment, canViewOwn, user)
 * @returns {Promise<Object>} Filter options
 */
export const fetchFilterOptions = async (permissions = {}) => {
    const { canViewGlobal, canViewDepartment, canViewOwn, user } = permissions;
    try {
        const [usersRes, servicesRes, standardsRes, productCategoriesRes, departmentsRes, branchesRes, taggingRes] = await Promise.all([
            api.get('/md/internal-users/option-internal-user').catch(() => ({ data: [] })),
            api.get('/crm/services/options').catch(() => ({ data: [] })),
            api.get('/md/standards').catch(() => ({ data: [] })),
            api.get('/md/product-category-options').catch(() => ({ data: [] })),
            api.get('/md/departement').catch(() => ({ data: [] })),
            (user?.id ? api.get(`/user-branches/user/${user.id}`) : api.get('/branches')).catch(() => ({ data: [] })),
            api.get('/md/tagging', { params: { moduleKey: 'companies' } }).catch(() => ({ data: [] }))
        ]);

        // Extract all users and filter out empty/null options
        const usersData = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || [];
        const userOptions = usersData
            .map(u => {
                const firstName = u.firstName || '';
                const lastName = u.lastName || '';
                const fullName = (firstName + ' ' + lastName).trim() || u.name || u.full_name || u.label || 'Unknown';
                return {
                    id: u.id || u.value,
                    name: fullName,
                    departmentId: u.departmentId || u.departementId || null,
                    status: u.status != null ? String(u.status) : '1'
                };
            })
            .filter(u => u.id != null && u.id !== '' && u.name && u.name !== 'Unknown' && u.name.trim() !== '');

        // Extract all services and filter out empty/null options
        const servicesData = servicesRes.data || [];
        const serviceOptions = servicesData
            .map(service => ({
                id: service.id || service.value,
                name: service.name || service.label || 'Unknown'
            }))
            .filter(service => service.id != null && service.id !== '' && service.name && service.name !== 'Unknown' && service.name.trim() !== '');

        // Extract all standards and filter out empty/null options
        const standardsData = Array.isArray(standardsRes.data) ? standardsRes.data : standardsRes.data?.data || [];
        const standardOptions = standardsData
            .map(std => ({
                id: std.id || std.name,
                name: std.name || std.standardName || 'Unknown'
            }))
            .filter(std => std.id != null && std.id !== '' && std.name && std.name !== 'Unknown' && std.name.trim() !== '');

        // Extract all product categories and filter out empty/null options
        const productCategoriesData = Array.isArray(productCategoriesRes.data) 
            ? productCategoriesRes.data 
            : productCategoriesRes.data?.data || [];
        const productCategoryOptions = productCategoriesData
            .map(cat => ({
                id: cat.id || cat.value,
                name: cat.name || cat.label || 'Unknown'
            }))
            .filter(cat => cat.id != null && cat.id !== '' && cat.name && cat.name !== 'Unknown' && cat.name.trim() !== '');

        // Extract all departments and filter out empty/null options
        const departmentsData = Array.isArray(departmentsRes.data)
            ? departmentsRes.data
            : departmentsRes.data?.data || [];
        let departmentOptions = departmentsData
            .map(dep => ({
                id: dep.id || dep.value,
                name: dep.name || dep.label || 'Unknown'
            }))
            .filter(dep => dep.id != null && dep.id !== '' && dep.name && dep.name !== 'Unknown' && dep.name.trim() !== '');

        const branchesData = branchesRes.data?.data ?? branchesRes.data ?? [];
        const branchOptions = Array.isArray(branchesData)
            ? branchesData
                .map(branch => ({
                    id: branch.id || branch.value,
                    name: branch.name || branch.branchName || branch.label || 'Unknown'
                }))
                .filter(branch => branch.id != null && branch.id !== '' && branch.name && branch.name !== 'Unknown' && branch.name.trim() !== '')
            : [];

        const taggingData = Array.isArray(taggingRes.data)
            ? taggingRes.data
            : taggingRes.data?.data || [];
        const taggingOptions = taggingData
            .map(tag => ({
                id: tag.id || tag.value,
                name: tag.name || tag.label || 'Unknown'
            }))
            .filter(tag => tag.id != null && tag.id !== '' && tag.name && tag.name !== 'Unknown' && tag.name.trim() !== '');

        // ✅ Apply permission-based filtering
        let filteredUserOptions = userOptions;
        let filteredDepartmentOptions = departmentOptions;

        if (!canViewGlobal) {
            if (canViewDepartment && user?.departmentId) {
                // Filter to only users from the user's department
                filteredUserOptions = userOptions.filter(userOption =>
                    String(userOption.departmentId) === String(user.departmentId)
                );
                // Filter to only the user's department
                filteredDepartmentOptions = departmentOptions.filter(dept =>
                    String(dept.id) === String(user.departmentId)
                );
            } else if (canViewOwn) {
                // Filter to only the current user
                filteredUserOptions = userOptions.filter(userOption =>
                    userOption.id === user.id
                );
                // No department filtering for view_own users
                filteredDepartmentOptions = [];
            }
        }

        return {
            users: filteredUserOptions,
            services: serviceOptions,
            standards: standardOptions,
            productCategories: productCategoryOptions,
            departments: filteredDepartmentOptions,
            branches: branchOptions,
            taggingOptions: taggingOptions
        };
    } catch (error) {
        return {
            users: [],
            services: [],
            standards: [],
            productCategories: [],
            departments: [],
            branches: [],
            taggingOptions: []
        };
    }
};

